/**
 * Платёжный календарь прогона М4 (БДДС, ТЗ §6.4). Скользящий прогноз остатков
 * денежных средств по неделям горизонта: остаток начала + ожидаемые поступления
 * − обязательные платежи = прогнозный остаток. Недели с кассовым разрывом
 * (Р-12) подсвечиваются. Рендерится только для прогонов модуля `cashflow`.
 */
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useGetEconCashCalendarQuery } from '@shared/api';
import { Button } from '@shared/ui/Button';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;
const Lead = styled.p`
  margin: 0;
  font-size: 13px;
  opacity: 0.8;
`;
const SimControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 12px;
`;
const WeekCard = styled.div<{ $gap: boolean }>`
  border: 1px solid
    ${({ $gap, theme }) => ($gap ? theme.colors.status.danger : theme.colors.border)};
  border-radius: 10px;
  padding: 12px 14px;
  background: ${({ $gap, theme }) =>
    $gap ? `${theme.colors.status.danger}14` : theme.colors.surface};
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const WeekHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  font-size: 14px;
`;
const WeekDates = styled.span`
  margin-left: 6px;
  font-weight: 500;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;
const GapBadge = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.status.danger};
  color: #fff;
`;
const Line = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12.5px;
`;
const Label = styled.span`
  opacity: 0.75;
`;
const Value = styled.span<{ $negative?: boolean }>`
  font-variant-numeric: tabular-nums;
  color: ${({ $negative, theme }) => ($negative ? theme.colors.status.danger : 'inherit')};
`;
const Forecast = styled.div<{ $negative: boolean }>`
  margin-top: 4px;
  padding-top: 6px;
  border-top: 1px dashed ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: space-between;
  font-weight: 600;
  font-size: 13.5px;
  color: ${({ $negative, theme }) => ($negative ? theme.colors.status.danger : 'inherit')};
`;
const SimBox = styled.div`
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const SimInput = styled.input`
  width: 96px;
  padding: 3px 6px;
  font-size: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 5px;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

/** Денежный формат ₽ с разделителями (без копеек для компактности). */
function money(v: number): string {
  return `${Math.round(v).toLocaleString('ru')} ₽`;
}

/** Диапазон недели «5–11 июл.» из ISO-даты начала (week_start). Дату парсим
 *  как локальную (без TZ-сдвига на день), конец = начало + 6 дней. `null`,
 *  если week_start отсутствует/некорректен — тогда показываем только номер. */
function weekRange(weekStart: string | null): string | null {
  if (!weekStart) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(weekStart);
  if (!m) return null;
  const start = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(start.getTime())) return null;
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const day = new Intl.DateTimeFormat('ru-RU', { day: 'numeric' });
  const dayMonth = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' });
  return start.getMonth() === end.getMonth()
    ? `${day.format(start)}–${dayMonth.format(end)}`
    : `${dayMonth.format(start)} – ${dayMonth.format(end)}`;
}

interface WeekDelta {
  inflowDelta: number;
  outflowDelta: number;
}

export function CashCalendar({ runId }: { runId: string }) {
  const { data, isLoading } = useGetEconCashCalendarQuery(runId);
  const weeks = useMemo(() => data?.weeks ?? [], [data?.weeks]);
  const [simMode, setSimMode] = useState(false);
  const [deltas, setDeltas] = useState<Record<number, WeekDelta>>({});

  const simulatedWeeks = useMemo(() => {
    if (weeks.length === 0) return [];
    const result = [];
    let runningOpening = weeks[0].opening_balance;

    for (let i = 0; i < weeks.length; i += 1) {
      const w = weeks[i];
      const d = deltas[w.week_no] ?? { inflowDelta: 0, outflowDelta: 0 };
      const opening = i === 0 ? w.opening_balance : runningOpening;
      const inflow = Math.max(0, w.expected_inflow + d.inflowDelta);
      const outflow = Math.max(0, w.obligatory_outflow + d.outflowDelta);
      const forecast = opening + inflow - outflow;
      runningOpening = forecast;
      result.push({
        ...w,
        opening_balance: opening,
        expected_inflow: inflow,
        obligatory_outflow: outflow,
        forecast_balance: forecast,
        is_gap: forecast < 0,
      });
    }
    return result;
  }, [weeks, deltas]);

  if (isLoading) return <Muted>Загрузка календаря…</Muted>;
  if (weeks.length === 0)
    return <Muted>Платёжный календарь не сформирован (нет данных ДДС за период).</Muted>;

  const displayWeeks = simMode ? simulatedWeeks : weeks;
  const gaps = displayWeeks.filter((w) => w.is_gap).length;

  function updateDelta(weekNo: number, patch: Partial<WeekDelta>) {
    setDeltas((prev) => {
      const cur = prev[weekNo] ?? { inflowDelta: 0, outflowDelta: 0 };
      return { ...prev, [weekNo]: { ...cur, ...patch } };
    });
  }

  /** Быстрое действие: перенести сумму кассового разрыва (платежей) на следующую неделю. */
  function deferGapToNextWeek(index: number) {
    const curWeek = displayWeeks[index];
    const nextWeek = displayWeeks[index + 1];
    if (!curWeek || !nextWeek || curWeek.forecast_balance >= 0) return;
    const deficit = Math.min(curWeek.obligatory_outflow, Math.abs(curWeek.forecast_balance));
    if (deficit <= 0) return;

    setDeltas((prev) => {
      const c = prev[curWeek.week_no] ?? { inflowDelta: 0, outflowDelta: 0 };
      const n = prev[nextWeek.week_no] ?? { inflowDelta: 0, outflowDelta: 0 };
      return {
        ...prev,
        [curWeek.week_no]: { ...c, outflowDelta: c.outflowDelta - deficit },
        [nextWeek.week_no]: { ...n, outflowDelta: n.outflowDelta + deficit },
      };
    });
  }

  return (
    <Wrap>
      <TopBar>
        <Lead>
          Прогноз остатков денежных средств на {displayWeeks.length} нед. вперёд: остаток начала +
          ожидаемые поступления − обязательные платежи.{' '}
          {gaps > 0 ? (
            <strong style={{ color: 'var(--danger, #d33)' }}>
              Кассовых разрывов: {gaps}.
            </strong>
          ) : (
            simMode && <strong>Кассовые разрывы устранены.</strong>
          )}
        </Lead>
        <SimControls>
          <Button
            type="button"
            $variant={simMode ? 'primary' : 'secondary'}
            $size="sm"
            onClick={() => setSimMode((v) => !v)}
          >
            {simMode ? 'Режим «Что если» включён' : 'Симулятор «Что если»'}
          </Button>
          {simMode && Object.keys(deltas).length > 0 && (
            <Button type="button" $variant="ghost" $size="sm" onClick={() => setDeltas({})}>
              Сбросить сдвиги
            </Button>
          )}
        </SimControls>
      </TopBar>

      <Grid>
        {displayWeeks.map((w, idx) => {
          const range = weekRange(w.week_start);
          const d = deltas[w.week_no] ?? { inflowDelta: 0, outflowDelta: 0 };
          return (
            <WeekCard key={w.id} $gap={w.is_gap}>
              <WeekHead>
                <span>
                  Неделя {w.week_no}
                  {range && <WeekDates>· {range}</WeekDates>}
                </span>
                {w.is_gap && <GapBadge>разрыв</GapBadge>}
              </WeekHead>
              <Line>
                <Label>Остаток начала</Label>
                <Value $negative={w.opening_balance < 0}>{money(w.opening_balance)}</Value>
              </Line>
              <Line>
                <Label>Поступления</Label>
                <Value>+{money(w.expected_inflow)}</Value>
              </Line>
              <Line>
                <Label>Платежи</Label>
                <Value>−{money(w.obligatory_outflow)}</Value>
              </Line>
              <Forecast $negative={w.forecast_balance < 0}>
                <span>Прогноз</span>
                <span>{money(w.forecast_balance)}</span>
              </Forecast>

              {simMode && (
                <SimBox>
                  <Line>
                    <Label>Δ Поступления</Label>
                    <SimInput
                      type="number"
                      step="10000"
                      value={d.inflowDelta || ''}
                      placeholder="0"
                      onChange={(e) =>
                        updateDelta(w.week_no, { inflowDelta: Number(e.target.value) || 0 })
                      }
                    />
                  </Line>
                  <Line>
                    <Label>Δ Платежи</Label>
                    <SimInput
                      type="number"
                      step="10000"
                      value={d.outflowDelta || ''}
                      placeholder="0"
                      onChange={(e) =>
                        updateDelta(w.week_no, { outflowDelta: Number(e.target.value) || 0 })
                      }
                    />
                  </Line>
                  {w.is_gap && idx < displayWeeks.length - 1 && (
                    <Button
                      type="button"
                      $variant="ghost"
                      $size="sm"
                      onClick={() => deferGapToNextWeek(idx)}
                    >
                      Перенести дефицит на нед. {displayWeeks[idx + 1].week_no} →
                    </Button>
                  )}
                </SimBox>
              )}
            </WeekCard>
          );
        })}
      </Grid>
    </Wrap>
  );
}

const Muted = styled.div`
  opacity: 0.7;
  font-size: 13px;
`;
